import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useOpenEvents } from '../../helpers/UseOpenEvents';
import { useViewableEventTypes } from "../../providers/ViewEventTypeProvider";
import './RefreshStatus.scss';

const RefreshStatus: React.FC = () => {
  const lastRefreshed = useSelector((state: any) => state.openEvents.lastRefreshed);
  const [showWarning, setShowWarning] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const refreshProcessing = "Refreshing...";
  const refreshText = "Last refresh: ";
  const refreshIcon = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path id="Vector" d="M12.264 24C8.91432 24 6.07661 22.8383 3.75089 20.5149C1.42632 18.1903 0.264038 15.3537 0.264038 12.0051C0.264038 8.65657 1.42632 5.81886 3.75089 3.492C6.07547 1.16514 8.91318 0.00114286 12.264 0C14.3018 0 16.2098 0.484 17.988 1.452C19.7652 2.42 21.1978 3.77143 22.2858 5.50629V0H24V8.96571H15.0343V7.25143H21.3772C20.4835 5.54286 19.2275 4.19257 17.6092 3.20057C15.9909 2.20857 14.2092 1.71314 12.264 1.71429C9.40689 1.71429 6.97832 2.71429 4.97832 4.71429C2.97832 6.71429 1.97832 9.14286 1.97832 12C1.97832 14.8571 2.97832 17.2857 4.97832 19.2857C6.97832 21.2857 9.40689 22.2857 12.264 22.2857C14.464 22.2857 16.4498 21.6571 18.2212 20.4C19.9926 19.1429 21.2355 17.4857 21.9498 15.4286H23.7703C23.0138 17.9954 21.5772 20.0657 19.4606 21.6394C17.344 23.2131 14.9452 24 12.264 24Z" fill="#848A8F"/>
  </svg>;
  const warningIcon = <svg width="20" height="20" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="fluent:warning-32-filled">
      <path id="Vector" d="M17.8725 4.07094C16.6238 1.81156 13.3763 1.81156 12.1275 4.07094L2.29034 21.8825C1.08284 24.0687 2.66534 26.75 5.16284 26.75H24.8382C27.3357 26.75 28.9182 24.0687 27.7097 21.8825L17.8725 4.07094ZM16.1719 21.125C16.1719 21.4358 16.0484 21.7339 15.8287 21.9536C15.6089 22.1734 15.3108 22.2969 15 22.2969C14.6892 22.2969 14.3912 22.1734 14.1714 21.9536C13.9516 21.7339 13.8282 21.4358 13.8282 21.125C13.8282 20.8142 13.9516 20.5161 14.1714 20.2964C14.3912 20.0766 14.6892 19.9531 15 19.9531C15.3108 19.9531 15.6089 20.0766 15.8287 20.2964C16.0484 20.5161 16.1719 20.8142 16.1719 21.125ZM15 8.9375C15.2487 8.9375 15.4871 9.03627 15.6629 9.21208C15.8388 9.3879 15.9375 9.62636 15.9375 9.875V17.375C15.9375 17.6236 15.8388 17.8621 15.6629 18.0379C15.4871 18.2137 15.2487 18.3125 15 18.3125C14.7514 18.3125 14.5129 18.2137 14.3371 18.0379C14.1613 17.8621 14.0625 17.6236 14.0625 17.375V9.875C14.0625 9.62636 14.1613 9.3879 14.3371 9.21208C14.5129 9.03627 14.7514 8.9375 15 8.9375Z" fill="#DE750C" />
    </g>
  </svg>;

  useEffect(() => {
    setShowWarning(false);
    const checkWarningStatus = () => {
      if (lastRefreshed) {
        const lastRefreshedDate = new Date(lastRefreshed);
        const currentDate = new Date();
        const timeDifference = (currentDate.getTime() - lastRefreshedDate.getTime()) / 60000; // 1 minute in milliseconds
        setShowWarning(timeDifference > 1);
      }
    };

    if (hasMounted) {
      // Run the check immediately when lastRefreshed changes
      checkWarningStatus();
    } else {
      // Set the flag to true after the initial mount
      setHasMounted(true);
    }

    // Set up to run the check after a minute
    const timer = setInterval(checkWarningStatus, 60000);

    return () => {
      clearTimeout(timer);
    };
  }, [lastRefreshed]);

  const viewEventTypes = useViewableEventTypes()?.viewEventTypes || [];
  const eventTypeFilters = useSelector((state: any) => state.filterSort.filters);
  const defaultSort = useSelector((state: any) => state.filterSort.sort);
  const dragAndDropEventList = useSelector((state: any) => state.filterSort.dragAndDropEventList);

  const { getOpenEventsFromSource } = useOpenEvents(viewEventTypes, eventTypeFilters, defaultSort, dragAndDropEventList);

  const onRefresh = () => {
    setIsRefreshing(true);
    getOpenEventsFromSource()
      .finally(() => setIsRefreshing(false));
  };

  return (
        <button className="titlebar-refresh-button" onClick={onRefresh} disabled={isRefreshing}>
        {showWarning && <span data-testid="warning-icon" className="warning-icon">{warningIcon}</span>}
        <span className="refresh-content">
            {lastRefreshed ? (
            <span className="titlebar-refresh-text" data-testid="refresh-text">
                {isRefreshing ? refreshProcessing : refreshText}
                {!isRefreshing && new Date(lastRefreshed).toLocaleString('en-US', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
                })}
            </span>
            ) : ''}
        </span>
        <span className="refresh-icon">{refreshIcon}</span>
        </button>
  );
};

export default RefreshStatus;